/**
 * Recorder(Jibri) 모드 유틸리티.
 *
 * Jibri 데몬은 녹화 시 headless Chrome으로 회의 페이지를 열 때 URL 해시에
 * `#config.iAmRecorder=true` 를 붙인다(순정 jitsi-meet 계약). 기존 record-ui가
 * 이 플래그로 동작하고 Jibri가 그대로 붙여주므로, wehago UI도 같은 해시를
 * 읽으면 Jibri 쪽 설정 변경 없이 recorder 모드를 켤 수 있다.
 *
 * recorder 모드에서는:
 *  - 프리조인 화면을 건너뛰고 자동 입장한다 (VideoRenewalPage)
 *  - Jibri가 Selenium으로 폴링하는 `window.APP.conference` 신호를 노출한다
 *  - (추후) 모든 UI 크롬을 숨긴 클린 레이아웃으로 렌더한다
 *
 * 일반 참여자에게 영향이 가지 않도록 모든 동작은 isRecorderMode() 뒤에 둔다.
 */

import { RECORDING_STATS_IDS } from '@/utils/recordingBot';

let cached: boolean | undefined;

/** 현재 페이지가 Jibri recorder 모드로 열렸는지 여부 (한 번 계산 후 캐시) */
export function isRecorderMode(): boolean {
  if (cached !== undefined) {
    return cached;
  }

  let result = false;
  try {
    // Jibri가 붙이는 해시: #config.iAmRecorder=true
    const hash = window.location.hash || '';
    if (/(?:^|[#&])config\.iAmRecorder=true(?:$|&)/.test(hash)) {
      result = true;
    }
    // 수동 테스트용 쿼리 파라미터: ?recorder=true
    if (!result) {
      const q = new URLSearchParams(window.location.search);
      if (q.get('recorder') === 'true') {
        result = true;
      }
    }
  } catch {
    result = false;
  }

  cached = result;
  return result;
}

/** 운영 wehago 서버로 취급하는 VITE_APP_TYPE 집합 (그 외는 개발/테스트) */
const PROD_APP_TYPES = new Set(['wehago', 'wehagov', 'wehagom', 'video2']);

/**
 * recorder가 광고할 stats-id.
 *
 * lib-jitsi-meet은 `config.statisticsId`를 그대로 presence의 `<stats-id>`로 싣고,
 * 원격 참가자는 `getStatsID()`로 이를 읽는다. wehago 진행 UI는 참가자 입장 시
 * `getStatsID()`가 이 값이면 recorder 봇으로 보고 UI에서 숨긴다
 * (ConferenceConnector._handleUserJoined, utils/recordingBot.ts).
 * 따라서 recorder는 이 statsId로 입장해야 다른 참가자 화면에 타일로 보이지 않는다.
 *
 * 운영/개발 서버에서 각각 'wehagorecord' / 'wehagorecord-dev'를 사용한다
 * (utils/recordingBot.ts RECORDING_STATS_IDS와 일치해야 함).
 */
export function recorderStatsId(): string {
  const appType = import.meta.env.VITE_APP_TYPE as string | undefined;
  return appType && PROD_APP_TYPES.has(appType) ? 'wehagorecord' : 'wehagorecord-dev';
}

type JitsiParticipantLike = {
  getId?: () => string;
  getTracks?: () => unknown[];
  isHidden?: () => boolean;
  getProperty?: (name: string) => unknown;
  hasFeature?: (feature: string) => boolean;
  getStatsID?: () => string | undefined;
};

type JitsiRoomLike = {
  isJoined?: () => boolean;
  getParticipants?: () => JitsiParticipantLike[];
};

/** ConferenceConnector가 CONFERENCE_JOINED 시 window._jitsiRoom에 노출한 room */
function getRoom(): JitsiRoomLike | null {
  return (window as unknown as { _jitsiRoom?: JitsiRoomLike })._jitsiRoom ?? null;
}

const JIGASI_FEATURE = 'http://jitsi.org/protocol/jigasi';

/**
 * Jibri 상태 체크에서 제외해야 하는 봇 참가자인지 판정한다.
 * 실사용자만 보고 판단한다.
 */
function isBotParticipant(p: JitsiParticipantLike): boolean {
  try {
    if (p.isHidden?.() === true) return true;
    const jigasi = p.getProperty?.('features_jigasi');
    if (jigasi === true || jigasi === 'true' || jigasi === 1 || jigasi === '1' || jigasi === 'T') {
      return true;
    }
    if (p.hasFeature?.(JIGASI_FEATURE) === true) return true;
    const statsId = p.getStatsID?.();
    if (typeof statsId === 'string' && RECORDING_STATS_IDS.has(statsId)) return true;
  } catch {
    /* 판정 실패 시 실사용자로 취급 */
  }
  return false;
}

/** 봇(Jigasi transcriber, 다른 recorder, hidden)을 제외한 실사용자 참가자 목록 */
function getRealParticipants(room: JitsiRoomLike | null): JitsiParticipantLike[] {
  return (room?.getParticipants?.() ?? []).filter((p) => !isBotParticipant(p));
}

/**
 * Jibri에 노출할 `_room`. 실제 room을 그대로 넘기되 `getParticipants()`만 봇 제외 목록으로
 * 바꾼다. 나머지(isJoined, room.members, addToPresence, leave 등)는 원본에 위임한다.
 */
function getRoomForJibri(): JitsiRoomLike {
  const room = getRoom();
  if (!room) {
    // 입장 전 폴링용 스텁. Jibri가 참가자 추적 리스너를 `_room.room`에 걸므로 그 형태도 갖춘다.
    return {
      isJoined: () => false,
      getParticipants: () => [],
      room: { members: {}, addListener: () => undefined },
    } as JitsiRoomLike;
  }
  return new Proxy(room as object, {
    get(target, prop, receiver) {
      if (prop === 'getParticipants') {
        return () => getRealParticipants(target as JitsiRoomLike);
      }
      const value = Reflect.get(target, prop, receiver);
      // 원본 메서드는 this가 원본 room이어야 하므로 바인딩해서 돌려준다.
      return typeof value === 'function' ? value.bind(target) : value;
    },
  }) as JitsiRoomLike;
}

/**
 * Jibri가 폴링하는 window.APP.conference 신호를 노출한다.
 * 순정 jitsi-meet(record-ui)이 제공하던 접근자들을 wehago room 객체로 재현하며,
 * 입장 전이면 안전한 기본값(0 / [] / false)을 반환한다.
 *
 * (참고) Jibri가 실제로 읽는 필드 집합은 배포 후 브라우저 로그로 확정한다.
 * 우선 record-ui 소스에서 추출한 후보 5종을 모두 노출해 방어적으로 커버한다.
 */
export function installRecorderAppShim(): void {
  if (!isRecorderMode()) {
    return;
  }

  const conference = {
    // Jibri(JibriSelenium)가 폴링하는 실제 스크립트는 APP.conference._room.isJoined()를
    // 호출한다(순정 jitsi-meet 계약). _room이 없으면 매 폴링마다 예외가 나서 입장 여부를
    // 영원히 확인 못 하고 타임아웃난다(실제 Jibri 브라우저 로그로 확인됨).
    // 상태 체크(numRemoteParticipantsMuted/Jigasi)도 이 _room.getParticipants()를 읽으므로
    get _room() {
      return getRoomForJibri();
    },
    isJoined(): boolean {
      return Boolean(getRoom()?.isJoined?.());
    },
    listMembersIds(): string[] {
      return getRealParticipants(getRoom()).map((p) => p.getId?.() ?? '');
    },
    listMembers(): JitsiParticipantLike[] {
      return getRealParticipants(getRoom());
    },
    get membersCount(): number {
      const room = getRoom();
      if (!room) {
        return 0;
      }
      // 반드시 같은(봇 제외) 목록 기준이어야 한다.
      return getRealParticipants(room).length + 1;
    },
    getNumberOfParticipantsWithTracks(): number {
      return getRealParticipants(getRoom()).filter((p) => (p.getTracks?.().length ?? 0) > 0).length;
    },
    getStats(): any {
      // Jibri가 30초마다 미디어 수신량을 체크하기 위해 호출 (NoMediaReceived 판단)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (getRoom() as any)?.connectionQuality?.getStats() ?? {};
    },
  };

  (window as unknown as { APP?: unknown }).APP = { conference };
}
